import { ChangeDetectorRef, Component, OnDestroy, OnInit, viewChild, output, inject, input } from '@angular/core';
import {DgModalComponent} from "@datagrupo2/dg-ng-util";
import {HttpService} from "../../../../../services/http/http.service";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import Swall from "sweetalert2";
import {replaceEdpointParams} from "../../../../../helpers/endpoints-helpers";
import {closeLoader, dispararLoader} from "../../../../../helpers/swall.helpers";
import {CONVITES_CONVIDAR_DADOS, LOTES} from "../../../../../core/config/endpoints";
import {DGValidators} from "../../../../../helpers/custom-validators";
import { catchRequestStatusError } from 'src/app/helpers/http-helpers';
import {LoteEntity} from "../../../../../entitys/lote.entity";
import {EMPTY, of, Subject} from "rxjs";
import {catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil} from "rxjs/operators";
import {MatIcon} from "@angular/material/icon";
import { NgxMaskDirective } from 'ngx-mask';


@Component({
  selector: 'modal-convites-eventos-dados',
  templateUrl: './modal-convites-eventos-dados.component.html',
  styleUrls: ['./modal-convites-eventos-dados.component.scss'],

  imports: [
    ReactiveFormsModule,
    DgModalComponent,
    MatIcon,
    NgxMaskDirective
  ]
})
export class ModalConvitesEventosDadosComponent implements OnInit, OnDestroy {
  private service = inject(HttpService);
  // Este modal guarda estado do autocomplete em campos comuns, mutados de
  // dentro de callbacks assíncronos (debounce, HTTP, setTimeout). Quando o host
  // é OnPush — caso da tela nova de check-ins — esses callbacks não sujam a
  // view e o dropdown nunca aparece. markForCheck() em cada mutação assíncrona.
  private cdr = inject(ChangeDetectorRef);


  readonly modal = viewChild.required<DgModalComponent>('modal');
  readonly id = input<number | string>('');
  readonly showRandomEmailgenerator = input<boolean>(false);
  readonly afterSave = output<{
    ingressoId: string | null;
    email: string | null;
}>();

  isEdited: boolean = false;

  lotesFiltrados: LoteEntity[] = [];
  loteSelecionado: LoteEntity | null = null;
  mostrarDropdown: boolean = false;
  loteBuscaControl = new FormControl('');
  private pendingAfterSavePayload: { ingressoId: string | null; email: string | null } | null = null;

  private destroy$ = new Subject<void>();

  form = new FormGroup({
    nome: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    cpf: new FormControl('', [DGValidators.CPF,(control)=>{
      if(control.value && control.value.length !== 11){
        return { cpf: true };
      }
      return DGValidators.CPF(control);
    }]),
    loteId: new FormControl<string | null>(null)
  });

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.form.valueChanges.subscribe(() => {
      this.isEdited = this.form.dirty;
    })
  }

  ngOnInit(): void {
    this.loteBuscaControl.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(busca => {
        const termo = busca?.trim() || '';
        if (!termo) {
          this.form.patchValue({ loteId: null });
          this.loteSelecionado = null;
          this.lotesFiltrados = [];
          this.mostrarDropdown = false;
          this.cdr.markForCheck();
          return EMPTY;
        }
        return this.service.get(replaceEdpointParams(LOTES, this.id()), { params: { filter: termo } }).pipe(
          map((resp: unknown) => this.extractLotesFromResponse(resp)),
          catchError(() => of([]))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe((lotes: LoteEntity[]) => {
      this.lotesFiltrados = lotes;
      this.mostrarDropdown = this.lotesFiltrados.length > 0;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private extractLotesFromResponse(resp: unknown): LoteEntity[] {
    const response = resp as any;
    const lotes = Array.isArray(response)
      ? response
      : Array.isArray(response?.content)
        ? response.content
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.content)
            ? response.data.content
            : [];

    return lotes as LoteEntity[];
  }

  private extractIngressoIdFromResponse(resp: unknown): string | null {
    const response = resp as any;
    return response?.ingressoId
      || response?.data?.ingressoId
      || response?.data?.data?.ingressoId
      || null;
  }

  beforeClose = () => {
    if (this.isEdited) {
      Swall.fire({
        icon: 'question',
        title: 'Sair sem salvar',
        text: 'Deseja descartar as alterações realizadas?',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Sim'
      }).then(conf => {
        if (conf.isConfirmed) {
          this.modal().close({ ignoreBeforeClose: true });
        }
      });
      return false;
    }

    return true;
  }

  open() {
    this.modal().open();
  }

  close() {
    this.modal().close();
  }

  onAfterClose() {
    this.clear();
    if (this.pendingAfterSavePayload) {
      this.afterSave.emit(this.pendingAfterSavePayload);
      this.pendingAfterSavePayload = null;
    }
  }

  clear() {
    this.form.reset({
      nome: '',
      email: '',
      cpf: '',
      loteId: null
    });
    this.form.markAsPristine();
    this.isEdited = false;
    this.loteBuscaControl.setValue('', { emitEvent: false });
    this.loteSelecionado = null;
    this.lotesFiltrados = [];
    this.mostrarDropdown = false;
  }

  selecionarLote(lote: LoteEntity | null) {
    this.loteSelecionado = lote;
    this.form.patchValue({ loteId: lote?.id ?? null });
    this.loteBuscaControl.setValue(lote?.nome ?? '', { emitEvent: false });
    this.mostrarDropdown = false;
  }

  ocultarDropdown() {
    setTimeout(() => { this.mostrarDropdown = false; this.cdr.markForCheck(); }, 200);
  }

  gerarEmailAleatorio() {
    const timestamp = Date.now();
    const email = `tempmail-${timestamp}@guedder.com`;
    this.form.patchValue({ email });
    this.form.get('email')?.markAsTouched();
  }

  salvar() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    dispararLoader()

    const cpfValue = this.form.value.cpf?.trim();
    const cpfClean = cpfValue ? cpfValue.replace(/\D/g, '') : '';

    const formData = {
      nome: this.form.value.nome?.trim() || null,
      email: this.form.value.email?.trim(),
      cpf: cpfClean && cpfClean.length === 11 ? cpfClean : null,
      loteId: this.form.value.loteId || null
    };

    this.service.post(replaceEdpointParams(CONVITES_CONVIDAR_DADOS, this.id()), formData).pipe(catchRequestStatusError({
      unknownErrorTitle:'Erro ao enviar convite'
    })).subscribe(
      resp => {
        // O loader é um Swall e não some sozinho ao fechar o modal. Faltava
        // este `closeLoader` — num 204 (convite que já existia) o caminho de
        // sucesso rodava inteiro e a tela ficava presa no loader.
        closeLoader();
        const ingressoId = this.extractIngressoIdFromResponse(resp);
        const email = this.form.value.email?.trim() || null;
        this.pendingAfterSavePayload = { ingressoId, email };
        this.modal().close({ignoreBeforeClose: true});
      }
    )
  }
}
